import {Alert, AlertTitle} from "@mui/material";
import {styled} from "@mui/system";
import {useSnackbar} from "notistack";
import React from "react";

/**
 * @typedef MessageObject
 * @property {string} title Título da mensagem (opcional)
 * @property {string} body Corpo da mensagem (opcional)
 * @property {array} bodyList Uma lista de mensagens que será exibida como tópicos (opcional)
 */

const Ul = styled("ul")({});
const Li = styled("li")({});

export default function useFeedback() {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();

    function content(key, message = "", severity) {
        const body =
            typeof message === "string" || message.type
                ? message
                : message.body;
        const title = message.title;
        const bodyList = message.bodyList;

        return (
            <Alert
                variant="filled"
                elevation={6}
                onClose={() => closeSnackbar(key)}
                severity={severity}
            >
                {title && <AlertTitle>{title}</AlertTitle>}
                {body}
                {bodyList?.length > 0 && (
                    <Ul sx={{margin: 0, paddingLeft: 2}}>
                        {bodyList.map((item, index) => (
                            <Li
                                key={index}
                                sx={{
                                    marginBottom:
                                        index !== bodyList.length - 1
                                            ? 1
                                            : undefined
                                }}
                            >
                                {item}
                            </Li>
                        ))}
                    </Ul>
                )}
            </Alert>
        );
    }

    const anchorOrigin = {horizontal: "center", vertical: "bottom"};

    /**
     * Exibe uma mensagem de sucesso que se apaga automaticamente após alguns segundos.
     * A mensagem pode ser uma string, um objeto no formato {title: "", body: "", bodyList:[""]}
     * ou um JSX
     *
     * @param {string | MessageObject } message Mensagem que será exibida
     * @param { object } options Opções passadas para o "notistack"
     */
    function showSuccess(message, options = {}) {
        enqueueSnackbar(message, {
            ...options,
            preventDuplicate: false,
            anchorOrigin,
            content: (key) => content(key, message, "success")
        });
    }

    /**
     * Exibe uma mensagem de informação que se apaga automaticamente após alguns segundos.
     * A mensagem pode ser uma string, um objeto no formato {title: "", body: "", bodyList:[""]}
     * ou um JSX
     *
     * @param {string | MessageObject } message Mensagem que será exibida
     * @param { object } options Opções passadas para o "notistack"
     */
    function showInfo(message, options = {}) {
        enqueueSnackbar(message, {
            ...options,
            preventDuplicate: true,
            anchorOrigin,
            content: (key) => content(key, message, "info")
        });
    }

    /**
     * Exibe uma mensagem de erro que permanece na tela até uma interação do usuário.
     * A mensagem pode ser uma string, um objeto no formato {title: "", body: "", bodyList:[""]}
     * ou {message: ""}
     * ou {message: "", errors:[{message:""}...]}
     * ou {message: "", errors:[{defaultMessage:""}...]}
     * ou um JSX
     *
     * @param {string | MessageObject } message Mensagem que será exibida
     * @param { object } options Opções passadas para o "notistack"
     */
    function showError(message, options = {persist: true}) {
        const newMessage = formatFeedbackError(message);
        enqueueSnackbar(newMessage, {
            ...options,
            anchorOrigin,
            onClose: (event, reason, key) => {
                if (reason === "clickaway" && options?.persist) {
                    closeSnackbar(key);
                }
            },
            content: (key) => content(key, newMessage, "error")
        });
    }

    return {showSuccess, showError, showInfo};
}

export function formatFeedbackError(message) {
    if (message instanceof ArrayBuffer) {
        try {
            message = JSON.parse(
                new TextDecoder("utf-8").decode(new Uint8Array(message))
            );
        } catch (error) {
            return "Não foi possível interpretar a resposta do conector.";
        }
    }

    if (typeof message === "string" && message.trim()) {
        return message.trim();
    }

    if (!message || typeof message !== "object") {
        return "Não foi possível concluir a operação.";
    }

    const body =
        `${message.message || ""}`.trim() ||
        `${message.error || ""}`.trim() ||
        "Não foi possível concluir a operação.";
    const bodyList = [];

    if (Array.isArray(message.errors)) {
        message.errors.forEach((error) => {
            const field = error?.field ? `${error.field}: ` : "";
            const detail = error?.message || error?.defaultMessage;
            if (detail) {
                bodyList.push(`${field}${detail}`);
            }
        });
    }

    if (message.identificador) {
        bodyList.push(`Identificador para suporte: ${message.identificador}`);
    }

    return {
        title: tituloErro(message),
        body,
        bodyList
    };
}

function tituloErro(message) {
    const status = Number(message.status);
    if (status === 401) return "Autenticação necessária";
    if (status === 403) return "Acesso não autorizado";
    if (status === 409) return "Operação não permitida";
    if (status === 412) return "Configuração incompleta";
    if (status >= 500) return "Falha no conector";
    return "Não foi possível concluir";
}
